import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { formatEventDate } from '../../../../helpers/formatData';
import { getDateIgnoringTimeAndTimezone } from '../../../../helpers/date';
import { isJobHasNotStarted, isJobSaved } from '../../../../helpers/jobs';
import Transactions from './Transactions';
import CreateEventModal from '../../createJob/createJob/ChooseEmployerEventStep/CreateEventModal';
import FormActionButtons from '../../../common/forms/FormActionButtons';
import Loader from '../../../common/ui/Loader';
import EventForm from './EventForm';
import UsersService from '../../../../services/UsersService';

export default class EventInfo extends Component {
  static propTypes = {
    jobDetails: PropTypes.shape({
      event_id: PropTypes.number,
      event: PropTypes.object,
      start_date: PropTypes.object.isRequired,
      time_zone_id: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      event_transactions: PropTypes.array
    }),
    showEditIcons: PropTypes.bool.isRequired,
    userEventsList: PropTypes.array.isRequired,
    createEventAction: PropTypes.func.isRequired,
    editJob: PropTypes.func.isRequired,
    editEventAction: PropTypes.func.isRequired,
    UIShowConfirm: PropTypes.func.isRequired,
    getJobDetails: PropTypes.func.isRequired
  };

  state = {
    isLoading: false,
    isChoosingEvent: false,
    selectedEvent: null,
    eventModalIsOpen: false,
    isEditingEvent: false
  };

  startLoading = () => {
    this.setState({ isLoading: true });
  };

  stopLoading = () => {
    this.setState({ isLoading: false });
  };

  chooseEvent = () => {
    this.setState({
      isChoosingEvent: true
    });
  };

  handleSelectEvent = selectedEventData => {
    const selectedEvent = selectedEventData && {
      ...selectedEventData,
      data: {
        ...selectedEventData.data,
        // Events list for user come from the server with dates in such format with timezone 'YYYY-MM-DDT00:00:00.000Z'.
        // We need to format this dates to 'YYYY-MM-DD' format to get date as it is in database.
        // We can't use moment for this, because we need to ignore timezones, so we just cut this date using substr.
        start_date: getDateIgnoringTimeAndTimezone(selectedEventData.data.start_date),
        end_date: getDateIgnoringTimeAndTimezone(selectedEventData.data.end_date)
      }
    };
    this.setState({ selectedEvent });
  };

  cancelChooseEvent = () => {
    this.setState({
      isChoosingEvent: false,
      selectedEvent: null
    });
  };

  submitSelectEvent = e => {
    e.preventDefault();
    const { selectedEvent } = this.state;
    if (!selectedEvent) {
      return;
    }
    const { editJob } = this.props;
    this.startLoading();
    editJob({ event_id: selectedEvent.value }).then(() => {
      this.setState({
        isChoosingEvent: false,
        selectedEvent: null
      });
      this.stopLoading();
    });
  };

  detachFromEvent = () => {
    this.props.UIShowConfirm('Detach job from event?', () => {
      this.startLoading();
      this.props.editJob({ event_id: null }).then(this.stopLoading);
    });
  };

  openEventModal = () => {
    this.setState({ eventModalIsOpen: true });
  };

  closeEventModal = () => {
    this.setState({ eventModalIsOpen: false });
  };

  submitCreateEvent = eventData => {
    const { start_date, end_date } = eventData;
    eventData.start_date = formatEventDate(start_date);
    eventData.end_date = formatEventDate(end_date);
    if (UsersService.isAdmin()) {
      eventData.user_id = this.props.jobDetails.user_id;
    }
    return this.props.createEventAction(eventData).then(event => {
      this.handleSelectEvent({
        label: event.name,
        value: event.id,
        data: event
      });
    });
  };

  startEditingEvent = () => {
    this.setState({ isEditingEvent: true });
  };

  stopEditingEvent = () => {
    this.setState({ isEditingEvent: false });
  };

  submitEditingEvent = eventData => {
    const { jobDetails: { event_id, id }, editEventAction, getJobDetails } = this.props;
    this.startLoading();
    editEventAction(event_id, eventData).then(() => {
      getJobDetails(id).then(() => {
        this.setState({
          isEditingEvent: false
        });
        this.stopLoading();
      });
    });
  };

  render () {
    const { jobDetails: { event_id, event, start_date, time_zone_id, event_transactions, status },
      userEventsList, showEditIcons } = this.props;
    const { isLoading, isChoosingEvent, selectedEvent, eventModalIsOpen, isEditingEvent } = this.state;
    const options = userEventsList.map(event => ({
      label: event.name,
      value: event.id,
      data: event
    }));
    const jobStatusIsSaved = isJobSaved(status);
    const jobHasNotStarted = isJobHasNotStarted(start_date, time_zone_id);
    const canChangeAttachmentToEvent = jobStatusIsSaved && jobHasNotStarted;
    return (
      <section className='event-info'>
        <h4 className='job-details-section-header'>Event</h4>
        { isLoading ? <div className='loader-wrapper'><Loader /></div> : (
          <Fragment>
            { event_transactions && <Transactions transactions={event_transactions} /> }
            { isChoosingEvent ? (
              <div className='mt-3'>
                <form onSubmit={this.submitSelectEvent}>
                  <Select
                    onChange={this.handleSelectEvent}
                    options={options}
                    placeholder='Choose'
                    value={selectedEvent}
                  />
                  <p className='my-2'>
                    Choose from list or
                    <button className='additional-link non-button' type='button' onClick={this.openEventModal}>
                      Create Another Event
                    </button>
                  </p>
                  <FormActionButtons
                    onClickCancel={this.cancelChooseEvent}
                    submitTitle='Ok'
                  />
                </form>
                <CreateEventModal
                  isOpen={eventModalIsOpen}
                  jobStartDate={start_date}
                  closeModal={this.closeEventModal}
                  submitCreateEvent={this.submitCreateEvent}
                />
              </div>
            ) : (
              <Fragment>
                { event_id ? (
                  <Fragment>
                    <EventForm
                      event={event}
                      showEditIcons={showEditIcons}
                      isEditing={isEditingEvent}
                      jobStartDate={start_date}
                      jobTimeZoneId={time_zone_id}
                      startEditingEvent={this.startEditingEvent}
                      stopEditingEvent={this.stopEditingEvent}
                      submitEditingEvent={this.submitEditingEvent}
                    />
                    { canChangeAttachmentToEvent && !isEditingEvent && (
                      <Fragment>
                        <button className='button button-primary assign my-2 mr-2 px-3' onClick={this.chooseEvent}>
                          Choose another event
                        </button>
                        <button className='button button-delete px-3' onClick={this.detachFromEvent}>
                          Detach job from event
                        </button>
                      </Fragment>
                    )}
                  </Fragment>
                ) : (
                  <Fragment>
                    <p className='mt-2'>There is no event related to this job.</p>
                    { canChangeAttachmentToEvent && (
                      <button className='button button-primary assign my-2 px-3' onClick={this.chooseEvent}>
                        Assign job to event
                      </button>
                    )}
                  </Fragment>
                )}
              </Fragment>
            )}
          </Fragment>
        )}
      </section>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/app/jobDetails/JobDetailsTab/EventInfo.js